// components/partial/MaterialsCollectionModal.jsx
// Collects material/product information when completing services
// Renders fields dynamically based on service category configuration

import React, { Component } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { HTTPService } from "../../services/httpService";
import MaterialScanningSection from './MaterialScanningSection';

const API = new HTTPService();

class MaterialsCollectionModal extends Component {
  constructor(props) {
    super(props);

    // Initialize state with empty values for each service
    const servicesData = {};
    if (props.services && props.services.length > 0) {
      props.services.forEach((service, index) => {
        servicesData[index] = {
          product_brand: '',
          product_name: '',
          roll_number: '',
          template: ''
        };
      });
    }

    this.state = {
      servicesData,
      inventoryItems: {}, // Keyed by service index: { 0: [...materials], 1: [...materials] }
      typeaheadSuggestions: {},
      activeTypeaheadField: null,
      errors: {},
      submitting: false,
      // Barcode scanner state
      showScanner: false,
      scannerServiceIndex: null,
      scannerFieldName: null,
      scannerError: null
    };

    this.html5QrCode = null;
  }

  componentWillUnmount() {
    // Clean up scanner if it exists
    this.stopScanner();
  }

  componentDidUpdate(prevProps) {
    // Reset state when services change
    if (prevProps.services !== this.props.services && this.props.services) {
      const servicesData = {};
      this.props.services.forEach((service, index) => {
        servicesData[index] = {
          product_brand: '',
          product_name: '',
          roll_number: '',
          template: ''
        };
      });
      this.setState({ servicesData, inventoryItems: {}, errors: {} });
    }
  }

  // Get field config for a service's category
  getFieldsForService = (service) => {
    const { categoryConfigs } = this.props;
    const config = categoryConfigs[service.service_category];

    if (!config || !config.data_collect_fields) {
      return [];
    }

    // Parse if string
    if (typeof config.data_collect_fields === 'string') {
      try {
        return JSON.parse(config.data_collect_fields);
      } catch (e) {
        console.error('Error parsing data_collect_fields:', e);
        return [];
      }
    }

    return config.data_collect_fields;
  };

  // Handle field value change
  handleFieldChange = (serviceIndex, fieldName, value) => {
    this.setState(prevState => ({
      servicesData: {
        ...prevState.servicesData,
        [serviceIndex]: {
          ...prevState.servicesData[serviceIndex],
          [fieldName]: value
        }
      },
      errors: {
        ...prevState.errors,
        [`${serviceIndex}_${fieldName}`]: null
      }
    }));

    // Trigger typeahead for brand and product fields
    if ((fieldName === 'product_brand' || fieldName === 'product_name') && value.length >= 2) {
      this.fetchTypeaheadSuggestions(serviceIndex, fieldName, value);
    } else {
      this.setState(prevState => ({
        typeaheadSuggestions: {
          ...prevState.typeaheadSuggestions,
          [`${serviceIndex}_${fieldName}`]: []
        }
      }));
    }
  };

  // Fetch typeahead suggestions from API
  fetchTypeaheadSuggestions = async (serviceIndex, fieldName, query) => {
    const { installerId } = this.props;

    try {
      const response = await API.get(`/materials/typeahead`, {
        installer_id: installerId,
        field: fieldName,
        query: query
      });

      this.setState(prevState => ({
        typeaheadSuggestions: {
          ...prevState.typeaheadSuggestions,
          [`${serviceIndex}_${fieldName}`]: response.data || []
        },
        activeTypeaheadField: `${serviceIndex}_${fieldName}`
      }));
    } catch (error) {
      console.error('Error fetching typeahead suggestions:', error);
    }
  };

  // Select a typeahead suggestion
  handleSelectSuggestion = (serviceIndex, fieldName, value) => {
    this.handleFieldChange(serviceIndex, fieldName, value);
    this.setState(prevState => ({
      typeaheadSuggestions: {
        ...prevState.typeaheadSuggestions,
        [`${serviceIndex}_${fieldName}`]: []
      },
      activeTypeaheadField: null
    }));
  };

  // Hide typeahead dropdown
  handleBlur = (serviceIndex, fieldName) => {
    // Delay to allow click on suggestion
    setTimeout(() => {
      this.setState(prevState => ({
        typeaheadSuggestions: {
          ...prevState.typeaheadSuggestions,
          [`${serviceIndex}_${fieldName}`]: []
        }
      }));
    }, 200);
  };

  // Handle inventory materials change from MaterialScanningSection
  handleInventoryMaterialsChange = (serviceIndex, materials) => {
    this.setState(prevState => ({
      inventoryItems: {
        ...prevState.inventoryItems,
        [serviceIndex]: materials
      }
    }));
  };

  // Auto-populate fields when a barcode is scanned
  handleMaterialScanned = (serviceIndex, materialData) => {
    this.setState(prevState => ({
      servicesData: {
        ...prevState.servicesData,
        [serviceIndex]: {
          ...prevState.servicesData[serviceIndex],
          product_brand: materialData.product_brand || prevState.servicesData[serviceIndex]?.product_brand || '',
          product_name: materialData.product_name || prevState.servicesData[serviceIndex]?.product_name || '',
          roll_number: materialData.roll_number || prevState.servicesData[serviceIndex]?.roll_number || ''
        }
      }
    }));
  };

  // Validate all required fields
  validate = () => {
    const { services } = this.props;
    const { servicesData } = this.state;
    const errors = {};
    let isValid = true;

    services.forEach((service, index) => {
      const fields = this.getFieldsForService(service);

      fields.forEach(field => {
        if (field.required && !servicesData[index]?.[field.field]?.trim()) {
          errors[`${index}_${field.field}`] = `${field.label} is required`;
          isValid = false;
        }
      });
    });

    this.setState({ errors });
    return isValid;
  };

  // Handle form submission
  handleSubmit = () => {
    if (!this.validate()) {
      return;
    }

    const { services, onSubmit } = this.props;
    const { servicesData, inventoryItems } = this.state;

    // Build materials data array matching services order
    const materialsDataArray = services.map((service, index) => {
      const fields = this.getFieldsForService(service);
      const data = {};

      // Only include fields that are configured for this category
      fields.forEach(field => {
        const value = servicesData[index]?.[field.field]?.trim();
        if (value) {
          data[field.field] = value;
        }
      });

      // Include scanned inventory items if present
      const scannedItems = inventoryItems[index] || [];
      if (scannedItems.length > 0) {
        // Extract inventory_item_id and quantity_used for the API
        data.inventory_items = scannedItems.map(item => ({
          inventory_item_id: item.inventory_item_id,
          quantity_used: parseFloat(item.quantity_used) || 0
        }));

        // Also include metadata for display/records
        if (scannedItems[0]) {
          data.product_brand = scannedItems[0].brand || data.product_brand;
          data.product_name = scannedItems[0].product_name || data.product_name;
          data.roll_number = scannedItems[0].barcode || data.roll_number;
          data.lot_number = scannedItems[0].lot_number || data.lot_number;
        }
      }

      return Object.keys(data).length > 0 ? data : null;
    });

    this.setState({ submitting: true });
    onSubmit(materialsDataArray);
  };

  // Check if device is mobile
  isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Render a single field
  renderField = (serviceIndex, field) => {
    const { servicesData, typeaheadSuggestions, errors } = this.state;
    const value = servicesData[serviceIndex]?.[field.field] || '';
    const suggestions = typeaheadSuggestions[`${serviceIndex}_${field.field}`] || [];
    const error = errors[`${serviceIndex}_${field.field}`];
    const showBarcodeScan = field.barcode_scan && this.isMobile();

    return (
      <Form.Group key={field.field} className="mb-3">
        <Form.Label>
          {field.label}
          {field.required && <span className="text-danger"> *</span>}
        </Form.Label>
        <div className="position-relative">
          <div className={showBarcodeScan ? 'd-flex' : ''}>
            <Form.Control
              type="text"
              value={value}
              onChange={(e) => this.handleFieldChange(serviceIndex, field.field, e.target.value)}
              onBlur={() => this.handleBlur(serviceIndex, field.field)}
              placeholder={`Enter ${field.label.toLowerCase()}`}
              isInvalid={!!error}
              className={showBarcodeScan ? 'flex-grow-1' : ''}
            />
            {showBarcodeScan && (
              <Button
                variant="outline-secondary"
                className="ml-2"
                onClick={() => this.handleBarcodeScan(serviceIndex, field.field)}
                title="Scan barcode"
              >
                <i className="fa fa-camera"></i>
              </Button>
            )}
          </div>

          {/* Typeahead dropdown */}
          {suggestions.length > 0 && (
            <div className="typeahead-dropdown">
              {suggestions.map((suggestion, idx) => (
                <div
                  key={idx}
                  className="typeahead-item"
                  onClick={() => this.handleSelectSuggestion(serviceIndex, field.field, suggestion)}
                >
                  {suggestion}
                </div>
              ))}
            </div>
          )}

          <Form.Control.Feedback type="invalid">
            {error}
          </Form.Control.Feedback>
        </div>
      </Form.Group>
    );
  };

  // Render fields for a single service
  renderServiceFields = (service, index) => {
    const fields = this.getFieldsForService(service);
    const { categoryConfigs, services } = this.props;
    const config = categoryConfigs[service.service_category];
    const isMultiService = services.length > 1;

    // Check if this category has data_collect enabled
    const showMaterialScanning = config?.data_collect;

    // If no fields and no material scanning, return null
    if (fields.length === 0 && !showMaterialScanning) {
      return null;
    }

    // Get expected quantity from service if available
    const expectedQty = service.expected_material_qty || null;

    return (
      <div key={index} className={isMultiService ? 'card mb-3' : ''}>
        {isMultiService && (
          <div className="card-header bg-light">
            <strong>{service.description || service.custom_description}</strong>
            <span className="text-muted ml-2">({service.service_category})</span>
          </div>
        )}
        <div className={isMultiService ? 'card-body' : ''}>
          {/* Material Scanning Section - AT THE TOP */}
          {showMaterialScanning && (
            <>
              {console.log('[MaterialsCollectionModal] Passing to MaterialScanningSection:', {
                serviceCategory: service.service_category,
                configCategoryId: config?.category_id,
                serviceCategoryId: service.category_id,
                resolvedCategoryId: config?.category_id || service.category_id,
                configKeys: config ? Object.keys(config) : []
              })}
              <MaterialScanningSection
                requestServiceId={service.request_service_id || service.service_id}
                serviceId={service.installer_service_id || service.service_id}
                categoryName={service.service_category}
                categoryId={config?.category_id || service.category_id}
                defaultUnitId={config?.default_unit_id}
                expectedQty={expectedQty}
                installerId={this.props.installerId}
                onMaterialsChange={(materials) => this.handleInventoryMaterialsChange(index, materials)}
                onMaterialScanned={(materialData) => this.handleMaterialScanned(index, materialData)}
              />
            </>
          )}

          {/* Only show text fields if NOT using material scanning (for categories without inventory tracking) */}
          {!showMaterialScanning && fields.length > 0 && (
            <div>
              {fields.map(field => this.renderField(index, field))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Start barcode scanner
  handleBarcodeScan = (serviceIndex, fieldName) => {
    this.setState({
      showScanner: true,
      scannerServiceIndex: serviceIndex,
      scannerFieldName: fieldName,
      scannerError: null
    }, () => {
      this.startScanner();
    });
  };

  // Initialize and start the scanner
  startScanner = () => {
    const scannerId = 'barcode-scanner-reader';

    // Wait for element to be in DOM
    setTimeout(() => {
      const scannerElement = document.getElementById(scannerId);
      if (!scannerElement) {
        console.error('Scanner element not found');
        return;
      }

      // Configure supported barcode formats
      const formatsToSupport = [
        Html5QrcodeSupportedFormats.CODE_128,
        Html5QrcodeSupportedFormats.CODE_39,
        Html5QrcodeSupportedFormats.CODE_93,
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.UPC_A,
        Html5QrcodeSupportedFormats.UPC_E,
        Html5QrcodeSupportedFormats.ITF,
        Html5QrcodeSupportedFormats.CODABAR,
        Html5QrcodeSupportedFormats.QR_CODE,
        Html5QrcodeSupportedFormats.DATA_MATRIX,
      ];

      this.html5QrCode = new Html5Qrcode(scannerId, { formatsToSupport });

      const config = {
        fps: 15,
        qrbox: { width: 300, height: 150 },
        aspectRatio: 1.777778,  // 16:9 aspect ratio
        disableFlip: false,
      };

      this.html5QrCode.start(
        { facingMode: "environment" }, // Use back camera
        config,
        this.handleScanSuccess,
        this.handleScanError
      ).catch(err => {
        console.error('Error starting scanner:', err);
        this.setState({
          scannerError: 'Unable to access camera. Please ensure camera permissions are granted.'
        });
      });
    }, 100);
  };

  // Stop the scanner
  stopScanner = async () => {
    if (this.html5QrCode) {
      try {
        const state = this.html5QrCode.getState();
        if (state === 2) { // SCANNING state
          await this.html5QrCode.stop();
        }
        this.html5QrCode.clear();
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
      this.html5QrCode = null;
    }
  };

  // Handle successful scan
  handleScanSuccess = (decodedText, decodedResult) => {
    console.log('Barcode scanned:', decodedText, decodedResult);

    const { scannerServiceIndex, scannerFieldName } = this.state;

    // Stop scanner and close modal
    this.stopScanner();

    // Update the field with scanned value
    this.handleFieldChange(scannerServiceIndex, scannerFieldName, decodedText);

    this.setState({
      showScanner: false,
      scannerServiceIndex: null,
      scannerFieldName: null,
      scannerError: null
    });
  };

  // Handle scan errors (called frequently, only log important errors)
  handleScanError = (errorMessage) => {
    // Only log actual errors, not "no barcode found" messages
    if (errorMessage &&
        !errorMessage.includes('No MultiFormat Readers') &&
        !errorMessage.includes('No barcode') &&
        !errorMessage.includes('NotFoundException')) {
      console.log('Scan error:', errorMessage);
    }
  };

  // Close scanner without scanning
  handleCloseScanner = () => {
    this.stopScanner();
    this.setState({
      showScanner: false,
      scannerServiceIndex: null,
      scannerFieldName: null,
      scannerError: null
    });
  };

  render() {
    const { show, onHide, services } = this.props;
    const { submitting, showScanner, scannerError } = this.state;

    if (!services || services.length === 0) {
      return null;
    }

    const isMultiService = services.length > 1;
    const serviceName = services[0]?.description || services[0]?.custom_description || 'Service';
    const categoryName = services[0]?.service_category || '';
    const title = isMultiService
      ? `Add Materials Used (${services.length} services)`
      : `Add Materials used for ${categoryName} ${serviceName}`;

    return (
      <>
      <Modal show={show} onHide={onHide} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {isMultiService && (
            <p className="text-muted mb-3">
              Please enter material information for each service below.
            </p>
          )}

          {services.map((service, index) => this.renderServiceFields(service, index))}

          {/* Services with no data collection */}
          {services.some(s => this.getFieldsForService(s).length === 0) && (
            <div className="text-muted mt-3">
              <i className="fa fa-info-circle mr-2"></i>
              Some services don't require material tracking.
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={this.handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <i className="fa fa-spinner fa-spin mr-2"></i>
                Completing...
              </>
            ) : (
              <>
                <i className="fa fa-check mr-2"></i>
                {isMultiService ? 'Complete All Services' : 'Complete Service'}
              </>
            )}
          </Button>
        </Modal.Footer>

        <style>{`
          .typeahead-dropdown {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            background: white;
            border: 1px solid #ced4da;
            border-top: none;
            border-radius: 0 0 4px 4px;
            max-height: 200px;
            overflow-y: auto;
            z-index: 1000;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          }

          .typeahead-item {
            padding: 8px 12px;
            cursor: pointer;
            border-bottom: 1px solid #f0f0f0;
          }

          .typeahead-item:hover {
            background-color: #f8f9fa;
          }

          .typeahead-item:last-child {
            border-bottom: none;
          }
        `}</style>
      </Modal>

      {/* Barcode Scanner Modal */}
      <Modal
        show={showScanner}
        onHide={this.handleCloseScanner}
        centered
        size="lg"
        className="barcode-scanner-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fa fa-barcode mr-2"></i>
            Scan Barcode
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-0">
          {scannerError ? (
            <div className="alert alert-danger m-3">
              <i className="fa fa-exclamation-triangle mr-2"></i>
              {scannerError}
            </div>
          ) : (
            <div className="scanner-container">
              <div id="barcode-scanner-reader" style={{ width: '100%' }}></div>
              <div className="text-center p-3">
                <p className="text-muted mb-2">
                  <i className="fa fa-lightbulb mr-2"></i>
                  Position the barcode within the box
                </p>
                <small className="text-muted">
                  Ensure good lighting and hold the camera steady
                </small>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={this.handleCloseScanner}>
            <i className="fa fa-times mr-2"></i>
            Cancel
          </Button>
        </Modal.Footer>

        <style>{`
          .barcode-scanner-modal .modal-body {
            min-height: 350px;
          }
          #barcode-scanner-reader {
            border: none !important;
          }
          #barcode-scanner-reader video {
            width: 100% !important;
            border-radius: 4px;
          }
          #barcode-scanner-reader__scan_region {
            min-height: 300px;
          }
          #barcode-scanner-reader__dashboard_section {
            display: none !important;
          }
        `}</style>
      </Modal>
      </>
    );
  }
}

export default MaterialsCollectionModal;
